import React, { useState, useRef, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Link, Navigate } from 'react-router-dom';
import HeaderMenu from './header-menu';
import TeamStatusBanner from './billing/TeamStatusBanner';
import ProtectedRoute from './components/ProtectedRoute';
import PageLoader from './components/PageLoader';
import AgentPolicyBoundary from './components/AgentPolicyBoundary';

// Lazy load components
const LoginPage = lazy(() => import('./LoginPage'));
const OAuthAuthorizationPage = lazy(() => import('./OAuthAuthorizationPage'));
const ConsolePage = lazy(() => import('./ConsolePage'));
const CreateAccountPage = lazy(() => import('./CreateAccountPage'));
const TwoFactorAuthPage = lazy(() => import('./2fa'));
const ForgotPassword = lazy(() => import('./ForgotPassword'));
const ResetPasswordConfirmation = lazy(
  () => import('./ResetPasswordConfirmation'),
);
const AddTeam = lazy(() => import('./AddTeam'));
const TeamPage = lazy(() => import('./TeamPage'));
const ProjectDetails = lazy(() => import('./projects/ProjectDetails'));
const SharePage = lazy(() => import('./team-files/SharePage'));
const WorkStreamPage = lazy(
  () => import('./projects/work-stream/WorkStreamPage'),
);
const LoopTaskDetailRoute = lazy(() => import('./LoopTaskDetailRoute'));
const TeamsListPage = lazy(() => import('./TeamsListPage'));
const AccountPage = lazy(() => import('./AccountPage'));
const NotificationsPage = lazy(() => import('./NotificationsPage'));
const PublicNotificationPreferencesPage = lazy(
  () => import('./PublicNotificationPreferencesPage'),
);
const HelpfulCommunicationsUnsubscribePage = lazy(
  () => import('./HelpfulCommunicationsUnsubscribePage'),
);
const AddAgent = lazy(() => import('./AddAgent'));
const AgentDetails = lazy(() => import('./AgentDetails'));
const AgentEdit = lazy(() => import('./AgentEdit'));
const NewLearn = lazy(() => import('./NewLearn'));
const Try = lazy(() => import('./Try'));
const Capabilities = lazy(() => import('./Capabilities'));
const AddSource = lazy(() => import('./AddSource'));
const KnowledgeSearch = lazy(() => import('./KnowledgeSearch'));
const NotFoundPage = lazy(() => import('./NotFoundPage'));
const AcceptTeamInvite = lazy(() => import('./AcceptTeamInvite'));
const TermsOfService = lazy(() => import('./TermsOfService'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
import { UserProvider, useUserContext } from './state/UserContext';
import { ApiProvider } from './state/ApiContext';
import { VoiceChannelProvider } from './voice/VoiceChannelProvider';
import { SystemProvider } from './state/SystemContext';
import { ThemeProvider, useTheme } from './state/ThemeContext';
import ChatDock from './components/chat/ChatDock';
import { ChatDockProvider } from './state/ChatDockContext';
import { MemberDetailProvider } from './state/MemberDetailContext';
import { ClientSocketProvider } from './state/ClientSocketContext';
import { ChatActivityProvider } from './state/ChatActivityContext';
import { ApprovalEventsProvider } from './state/ApprovalEventsContext';
import { FaMoon, FaSun } from 'react-icons/fa';
import {
  HiddenFeaturesProvider,
  useHiddenFeatures,
} from './contexts/HiddenFeaturesContext';
import { SessionCheckProvider } from './contexts/SessionCheckContext';
import './App.css';
// After App.css: it undoes that file's nav-oriented list reset for rendered
// markdown, and both use single-class specificity, so order decides.
import './markdown-content.css';
import './theme.css';
import './contexts/hidden-features.css';
import './loading-spinner.css';
import ModalOverlay from './components/ModalOverlay';
import {
  ConsoleHierarchyOptionsProvider,
  HierarchyExtensionProvider,
} from './components/console-hierarchy';
import {
  setCurrentWebPushUser,
  startWebPushLifecycle,
} from './notifications/web-push-client';

const WebPushLifecycle = (): null => {
  const { isLoggedIn, user } = useUserContext();
  const userId = isLoggedIn ? (user?.id ?? user?.email ?? null) : null;

  useEffect(() => {
    if (userId === null) {
      setCurrentWebPushUser(null);
      return;
    }
    return startWebPushLifecycle(userId);
  }, [userId]);

  return null;
};

const ConsoleRouteProviders: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <MemberDetailProvider>
    <ConsoleHierarchyOptionsProvider>
      <HierarchyExtensionProvider>{children}</HierarchyExtensionProvider>
    </ConsoleHierarchyOptionsProvider>
  </MemberDetailProvider>
);

// Theme toggle button component
const ThemeToggle: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { enableHiddenFeatures } = useHiddenFeatures();
  const [clickCount, setClickCount] = useState(0);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle theme toggle clicks
  const handleThemeToggleClick = () => {
    // Toggle theme
    toggleTheme();

    // Increment click counter
    setClickCount((prevCount) => {
      const newCount = prevCount + 1;

      // Reset timer on each click
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }

      // Set a new timer to reset the counter after 3 seconds of inactivity
      clickTimerRef.current = setTimeout(() => {
        setClickCount(0);
      }, 3000);

      // If we've reached 10 clicks, enable hidden features
      if (newCount === 10) {
        enableHiddenFeatures();
        setClickCount(0);
        if (clickTimerRef.current) {
          clearTimeout(clickTimerRef.current);
        }
      }

      return newCount;
    });
  };

  // Clean up the timer when component unmounts
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="theme-toggle-container">
      <button
        className="theme-mode-text clickable"
        onClick={handleThemeToggleClick}
        aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
        {/* Both variants stay mounted so the label can crossfade with the rest
            of the page; swapping the text would snap. See .theme-swap in
            @delegate/theme. The aria-label above is the accessible name. */}
        <span className="theme-swap" aria-hidden="true">
          <span className="theme-swap-light">dark mode</span>
          <span className="theme-swap-dark">light mode</span>
        </span>
      </button>
      <button
        className="theme-toggle-button"
        onClick={handleThemeToggleClick}
        aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
        <span className="theme-swap" aria-hidden="true">
          <span className="theme-swap-light">
            <FaMoon />
          </span>
          <span className="theme-swap-dark">
            <FaSun />
          </span>
        </span>
      </button>
    </div>
  );
};

const App: React.FC = () => {
  const location = useLocation();
  const showLogo = location.pathname !== '/';
  const [showProModal, setShowProModal] = useState(false);

  // Footer links go to standalone content pages; reset scroll so they open
  // at the top rather than keeping the previous page's scroll position.
  const scrollToTop = () => window.scrollTo({ top: 0, left: 0 });

  // Refs for scroll animation elements
  const animatedElementsRef = useRef<Array<HTMLElement | null>>([]);

  // Effect for Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            // Optional: Unobserve after animation to improve performance
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        // rootMargin: '-50px 0px -50px 0px', // Adjust margin if needed
      },
    );

    // Observe elements
    const currentElements = animatedElementsRef.current.filter(
      (el) => el !== null,
    ) as HTMLElement[];
    currentElements.forEach((el) => observer.observe(el));

    // Cleanup observer on component unmount
    return () => {
      currentElements.forEach((el) => observer.unobserve(el));
    };
  }, [location.pathname]); // Re-run if path changes, especially for SPA navigation

  // Helper function to add refs to the array
  const addToRefs = (el: HTMLElement | null) => {
    if (el && !animatedElementsRef.current.includes(el)) {
      animatedElementsRef.current.push(el);
    }
  };

  // Reset refs when navigating away from homepage
  useEffect(() => {
    if (location.pathname !== '/') {
      animatedElementsRef.current = [];
    }
  }, [location.pathname]);

  return (
    <ApiProvider>
      <VoiceChannelProvider>
        <SystemProvider>
          <UserProvider>
            <WebPushLifecycle />
            <ThemeProvider>
              <HiddenFeaturesProvider>
                <SessionCheckProvider>
                  <ChatDockProvider>
                    <ClientSocketProvider>
                      <ApprovalEventsProvider>
                        <ChatActivityProvider>
                          <ConsoleRouteProviders>
                            <header className="header">
                              <HeaderMenu showLogo={showLogo} />
                            </header>

                            <TeamStatusBanner />

                            <Suspense fallback={<PageLoader />}>
                              <Routes>
                                {/* Marketing lives in the separate marketing/ site and is not
                                part of this image, so "/" is not a landing page here - it
                                is the way in. Signed-in visitors go straight to their
                                console; ProtectedRoute bounces everyone else to
                                /login?redirectUrl=/console, so they arrive where they
                                were headed. Redirecting to /login directly would strand
                                a signed-in user on a login form. */}
                                <Route
                                  path="/"
                                  element={<Navigate to="/console" replace />}
                                />
                                <Route
                                  path="/login"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <LoginPage />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/oauth/authorize"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <OAuthAuthorizationPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/create-account"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <CreateAccountPage />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/share/:token"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <SharePage />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/console/team/:teamId/accept-invite"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <AcceptTeamInvite />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/2fa"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <TwoFactorAuthPage />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/forgot-password"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <ForgotPassword />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/reset-password"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <ResetPasswordConfirmation />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/terms-of-service"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <TermsOfService />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/privacy-policy"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <PrivacyPolicy />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/notification-preferences"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <PublicNotificationPreferencesPage />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/notification-preferences/unsubscribe"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <HelpfulCommunicationsUnsubscribePage />
                                    </Suspense>
                                  }
                                />
                                {/* Protected Routes */}
                                <Route
                                  path="/console"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <ConsolePage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/notifications"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <NotificationsPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/teams"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamsListPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/add"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <AddTeam />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/secrets"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/secrets/:secretId"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/agents"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/project-list"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/knowledge"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/workspaces"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/skills-policy"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/network-policy"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/sso"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/settings"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/users"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/roles"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/scheduled-tasks"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:teamId/scheduled-tasks/loops/:loopTaskId"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <LoopTaskDetailRoute />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/model-providers"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/metrics"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/work"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/billing"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/billing/plan"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/files"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/chat"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/projects"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                {/* Project Details pages live under /details/ so
                                they never collide with the board's
                                /cards/:cardId routes below */}
                                {[
                                  'metrics',
                                  'budgets',
                                  'members',
                                  'agents',
                                  'files',
                                  'work',
                                  'settings',
                                ].map((tab) => (
                                  <Route
                                    key={tab}
                                    path={`/console/team/:teamId/projects/:projectId/details/${tab}`}
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <ProjectDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                ))}
                                <Route
                                  path="/console/team/:id/projects/:projectId"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/projects/:projectId/cards/:cardId"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/work-streams/:workStreamId"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <WorkStreamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/projects/:projectId/wiki/*"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/projects/:projectId/databases/*"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/projects/:projectId/files"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route element={<AgentPolicyBoundary />}>
                                  <Route
                                    path="/console/team/:teamId/agent/add"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AddAgent />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/metrics"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/skills"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/knowledge"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/knowledge/:sourceId"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/logs"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/work"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/action-log/:metricId"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/edit"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentEdit />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/knowledge/:sourceId/learn"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <NewLearn />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/try"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <Try />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/capabilities"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <Capabilities />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/knowledge/add"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AddSource />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/knowledge/search"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/knowledge/documents"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/knowledge/graph"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/memories"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/conversations"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/communications"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/activities"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/communications/conversations"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/communications/email-account"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/communications/policies"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/conversations/:conversationId"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/testing"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/testing/:testingId"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/testing/:testingId/results"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/test/results"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/tools"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/secrets"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/secrets/:secretId"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/channels"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/scheduled-tasks"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/workspaces"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/model"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/console/team/:teamId/agent/:agentId/settings"
                                    element={
                                      <ProtectedRoute>
                                        <Suspense fallback={<PageLoader />}>
                                          <AgentDetails />
                                        </Suspense>
                                      </ProtectedRoute>
                                    }
                                  />
                                </Route>
                                <Route
                                  path="/console/team/:teamId/knowledge/add"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <AddSource />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:teamId/knowledge/search"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <KnowledgeSearch />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/team/:id/knowledge/:sourceId"
                                  element={
                                    <ProtectedRoute>
                                      <Suspense fallback={<PageLoader />}>
                                        <TeamPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/console/account"
                                  element={
                                    <ProtectedRoute retainAfterSessionExit>
                                      <Suspense fallback={<PageLoader />}>
                                        <AccountPage />
                                      </Suspense>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="*"
                                  element={
                                    <Suspense fallback={<PageLoader />}>
                                      <NotFoundPage />
                                    </Suspense>
                                  }
                                />
                              </Routes>
                            </Suspense>

                            {/* Pro Coming Soon Modal (Not animated) */}
                            {showProModal && (
                              <ModalOverlay
                                className="delete-modal-overlay"
                                onClick={() => setShowProModal(false)}>
                                <div
                                  className="delete-modal-container"
                                  onClick={(e) => e.stopPropagation()}>
                                  <h3
                                    style={{
                                      marginTop: '0',
                                      textAlign: 'left',
                                    }}>
                                    Pro Coming Soon
                                  </h3>
                                  <p style={{ textAlign: 'left' }}>
                                    Sign up for Free now, and we will email you
                                    when Pro is ready to go.
                                  </p>
                                  <div
                                    className="delete-modal-actions"
                                    style={{ justifyContent: 'flex-end' }}>
                                    <Link to="/create-account">
                                      <button>Continue to Free</button>
                                    </Link>
                                  </div>
                                </div>
                              </ModalOverlay>
                            )}

                            <section>
                              <footer>
                                <div className="footer-content">
                                  <div>
                                    <p
                                      style={{
                                        textAlign: 'left',
                                        fontSize: '0.85rem',
                                      }}>
                                      AI can make mistakes.{' '}
                                      {/* Server-rendered, like the
                                          Documentation link below. */}
                                      <a href="/docs/concepts/ai-management-and-expectations">
                                        Learn More
                                      </a>
                                    </p>
                                    <p
                                      style={{
                                        textAlign: 'left',
                                        fontSize: '0.85rem',
                                      }}>
                                      <Link
                                        to="/terms-of-service"
                                        onClick={scrollToTop}>
                                        Terms of Service
                                      </Link>
                                      {' | '}
                                      <Link
                                        to="/privacy-policy"
                                        onClick={scrollToTop}>
                                        Privacy Policy
                                      </Link>
                                      {' | '}
                                      {/* The documentation is rendered by the
                                          server, not by this bundle, so it
                                          needs a real navigation - a <Link>
                                          would hand /docs to the router, which
                                          has no such route and would answer
                                          with the not-found page. */}
                                      <a href="/docs">Documentation</a>
                                    </p>
                                  </div>
                                  <ThemeToggle />
                                </div>
                              </footer>
                            </section>

                            {/* Intercom-style persistent agent chat dock */}
                            <ChatDock />
                          </ConsoleRouteProviders>
                        </ChatActivityProvider>
                      </ApprovalEventsProvider>
                    </ClientSocketProvider>
                  </ChatDockProvider>
                </SessionCheckProvider>
              </HiddenFeaturesProvider>
            </ThemeProvider>
          </UserProvider>
        </SystemProvider>
      </VoiceChannelProvider>
    </ApiProvider>
  );
};

export default App;
